import { useMemo, useState } from "react";
import { IndianRupee, Percent, TrendingUp, Calculator, PieChart, Info } from "lucide-react";
import Seo from "../components/Seo";

const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Approximate 2026 stamp-duty rates per state (verify with sub-registrar before purchase)
const STAMP_DUTY = [
    { state: "Maharashtra", metro: 6, nonMetro: 5, registration: 1, women: 5 },
    { state: "Delhi",       metro: 6, nonMetro: 6, registration: 1, women: 4 },
    { state: "Karnataka",   metro: 5, nonMetro: 5, registration: 1, women: 5 },
    { state: "Tamil Nadu",  metro: 7, nonMetro: 7, registration: 4, women: 7 },
    { state: "Telangana",   metro: 5.5, nonMetro: 5.5, registration: 0.5, women: 5.5 },
    { state: "Gujarat",     metro: 4.9, nonMetro: 4.9, registration: 1, women: 3.9 },
    { state: "Uttar Pradesh", metro: 7, nonMetro: 7, registration: 1, women: 6 },
    { state: "Rajasthan",   metro: 6, nonMetro: 5, registration: 1, women: 5 },
    { state: "West Bengal", metro: 6, nonMetro: 5, registration: 1, women: 6 },
    { state: "Haryana",     metro: 7, nonMetro: 5, registration: 1, women: 5 },
];

/* ---------------- Individual calculators ---------------- */

function LoanEligibility() {
    const [income, setIncome] = useState(100000);
    const [otherEmi, setOtherEmi] = useState(0);
    const [tenure, setTenure] = useState(20);
    const [rate, setRate] = useState(8.5);

    // FOIR = 55% of net income for most banks; deducting other EMIs
    const maxEmi = useMemo(() => Math.max(0, income * 0.55 - otherEmi), [income, otherEmi]);
    // P = EMI * ((1 + r)^n - 1) / (r * (1 + r)^n)  where r = monthly rate, n = months
    const eligibleLoan = useMemo(() => {
        const r = rate / 12 / 100;
        const n = tenure * 12;
        if (!r) return 0;
        const factor = (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
        return maxEmi * factor;
    }, [maxEmi, tenure, rate]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="calc-loan-eligibility">
            <div className="md:col-span-3 space-y-5">
                <RangeInput label="Monthly income (₹)" value={income} onChange={setIncome} min={20000} max={1000000} step={5000} testid="calc-loan-income" />
                <RangeInput label="Other running EMIs (₹)" value={otherEmi} onChange={setOtherEmi} min={0} max={200000} step={1000} testid="calc-loan-other-emi" />
                <RangeInput label="Loan tenure (years)" value={tenure} onChange={setTenure} min={5} max={30} step={1} testid="calc-loan-tenure" />
                <RangeInput label="Interest rate (%)" value={rate} onChange={setRate} min={7} max={13} step={0.05} testid="calc-loan-rate" isDecimal />
            </div>
            <div className="md:col-span-2">
                <div className="rounded-2xl bg-gradient-to-br from-[#001a3d] to-[#002657] text-on-image p-6 h-full">
                    <div className="text-[10px] uppercase tracking-widest text-gold-300 font-bold">Eligible Home Loan</div>
                    <div className="font-heading text-3xl sm:text-4xl font-extrabold text-white mt-2">{inr(eligibleLoan)}</div>
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-sm">
                        <Row label="Max monthly EMI" value={inr(maxEmi)} />
                        <Row label="FOIR (55% cap)" value={`${((maxEmi / income) * 100).toFixed(0)}%`} />
                        <Row label="Tenure" value={`${tenure} yrs`} />
                        <Row label="Rate p.a." value={`${rate.toFixed(2)}%`} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function StampDuty() {
    const [state, setState] = useState("Maharashtra");
    const [price, setPrice] = useState(5000000);
    const [isMetro, setIsMetro] = useState(true);
    const [buyerIsWoman, setBuyerIsWoman] = useState(false);

    const s = STAMP_DUTY.find((x) => x.state === state);
    const stampRate = buyerIsWoman ? s.women : (isMetro ? s.metro : s.nonMetro);
    const stampAmt = (price * stampRate) / 100;
    const regAmt = (price * s.registration) / 100;
    const total = stampAmt + regAmt;

    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="calc-stamp-duty">
            <div className="md:col-span-3 space-y-5">
                <div>
                    <label className="text-xs font-bold text-navy-500/70 uppercase tracking-wider">State</label>
                    <select value={state} onChange={(e) => setState(e.target.value)} data-testid="calc-stamp-state" className="w-full mt-1 bg-white border border-navy-500/15 rounded-md px-3 py-2.5 text-sm text-navy-500">
                        {STAMP_DUTY.map((x) => <option key={x.state} value={x.state}>{x.state}</option>)}
                    </select>
                </div>
                <RangeInput label="Property price (₹)" value={price} onChange={setPrice} min={500000} max={200000000} step={100000} testid="calc-stamp-price" />
                <div className="flex flex-wrap gap-4 items-center">
                    <label className="inline-flex items-center gap-2 text-sm text-navy-500 font-semibold cursor-pointer">
                        <input type="checkbox" checked={isMetro} onChange={(e) => setIsMetro(e.target.checked)} data-testid="calc-stamp-metro" className="accent-gold-500" />
                        Metro / municipal area
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-navy-500 font-semibold cursor-pointer">
                        <input type="checkbox" checked={buyerIsWoman} onChange={(e) => setBuyerIsWoman(e.target.checked)} data-testid="calc-stamp-woman" className="accent-gold-500" />
                        Buyer is a woman (rebate)
                    </label>
                </div>
                <div className="text-[11px] text-navy-500/50 flex items-start gap-1.5 leading-relaxed">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    Rates are indicative 2026 slabs. Confirm exact stamp duty and cess with your state&apos;s sub-registrar.
                </div>
            </div>
            <div className="md:col-span-2">
                <div className="rounded-2xl bg-gradient-to-br from-[#001a3d] to-[#002657] text-on-image p-6 h-full">
                    <div className="text-[10px] uppercase tracking-widest text-gold-300 font-bold">Total registry cost</div>
                    <div className="font-heading text-3xl sm:text-4xl font-extrabold text-white mt-2">{inr(total)}</div>
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-sm">
                        <Row label={`Stamp duty (${stampRate}%)`} value={inr(stampAmt)} />
                        <Row label={`Registration (${s.registration}%)`} value={inr(regAmt)} />
                        <Row label="% of property price" value={`${((total / price) * 100).toFixed(2)}%`} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ROICalculator() {
    const [buyPrice, setBuyPrice] = useState(5000000);
    const [monthlyRent, setMonthlyRent] = useState(15000);
    const [appreciation, setAppreciation] = useState(8);
    const [years, setYears] = useState(5);
    const [expenses, setExpenses] = useState(15); // % of rent that goes to maintenance/tax/vacancy

    const netAnnualRent = monthlyRent * 12 * (1 - expenses / 100);
    const rentalYield = (netAnnualRent / buyPrice) * 100;
    const futureValue = buyPrice * Math.pow(1 + appreciation / 100, years);
    const capitalGain = futureValue - buyPrice;
    const totalRent = netAnnualRent * years;
    const totalReturn = capitalGain + totalRent;
    const totalROI = (totalReturn / buyPrice) * 100;
    const cagr = (Math.pow((buyPrice + totalReturn) / buyPrice, 1 / years) - 1) * 100;

    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-testid="calc-roi">
            <div className="md:col-span-3 space-y-5">
                <RangeInput label="Buy price (₹)" value={buyPrice} onChange={setBuyPrice} min={500000} max={200000000} step={100000} testid="calc-roi-buy" />
                <RangeInput label="Expected monthly rent (₹)" value={monthlyRent} onChange={setMonthlyRent} min={0} max={500000} step={500} testid="calc-roi-rent" />
                <RangeInput label="Annual price appreciation (%)" value={appreciation} onChange={setAppreciation} min={0} max={20} step={0.5} testid="calc-roi-appreciation" isDecimal />
                <RangeInput label="Holding period (years)" value={years} onChange={setYears} min={1} max={30} step={1} testid="calc-roi-years" />
                <RangeInput label="Maintenance / tax / vacancy (%)" value={expenses} onChange={setExpenses} min={0} max={50} step={1} testid="calc-roi-expenses" />
            </div>
            <div className="md:col-span-2">
                <div className="rounded-2xl bg-gradient-to-br from-[#001a3d] to-[#002657] text-on-image p-6 h-full">
                    <div className="text-[10px] uppercase tracking-widest text-gold-300 font-bold">Total ROI in {years} yrs</div>
                    <div className="font-heading text-3xl sm:text-4xl font-extrabold text-white mt-2">{totalROI.toFixed(1)}%</div>
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-sm">
                        <Row label="Rental yield (net)" value={`${rentalYield.toFixed(2)}%`} />
                        <Row label="Projected value" value={inr(futureValue)} />
                        <Row label="Capital gain" value={inr(capitalGain)} />
                        <Row label={`Total rent (${years} yr)`} value={inr(totalRent)} />
                        <Row label="Annualised CAGR" value={`${cagr.toFixed(2)}%`} />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------------- Shared UI primitives ---------------- */

function RangeInput({ label, value, onChange, min, max, step, testid, isDecimal }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-navy-500/70 uppercase tracking-wider">{label}</label>
                <input
                    type="number"
                    value={value}
                    onChange={(e) => onChange(isDecimal ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
                    data-testid={`${testid}-input`}
                    className="w-32 text-right px-2 py-1 rounded border border-navy-500/15 text-sm font-bold text-navy-500 focus:border-gold-500/60 focus:outline-none"
                />
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(isDecimal ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
                data-testid={testid}
                className="w-full accent-gold-500"
            />
            <div className="flex justify-between text-[10px] text-navy-500/40 mt-1">
                <span>{typeof min === "number" && min >= 100000 ? inr(min) : min}</span>
                <span>{typeof max === "number" && max >= 100000 ? inr(max) : max}</span>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-white/65 text-xs">{label}</span>
            <span className="text-gold-300 font-bold">{value}</span>
        </div>
    );
}

/* ---------------- Page shell ---------------- */

const TABS = [
    { key: "loan",  label: "Loan Eligibility", icon: Calculator, comp: LoanEligibility },
    { key: "stamp", label: "Stamp Duty",       icon: Percent,    comp: StampDuty },
    { key: "roi",   label: "ROI / Yield",      icon: TrendingUp, comp: ROICalculator },
];

export default function Calculators() {
    const [tab, setTab] = useState("loan");
    const Active = TABS.find((t) => t.key === tab)?.comp || LoanEligibility;
    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="Property Calculators — Loan, Stamp Duty & ROI"
                description="Free online calculators for home loan eligibility, state-wise stamp duty & registration cost, and rental ROI / capital appreciation forecasts. Powered by Global Property Mart."
                path="/calculators"
                keywords="home loan eligibility calculator, stamp duty calculator India, ROI calculator real estate, property calculators"
            />

            <section className="text-on-image relative bg-gradient-to-br from-[#001a3d] via-[#002657] to-[#12234b] border-b border-gold-500/10">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <div className="label-overline mb-3 inline-flex items-center gap-2">
                        <PieChart className="w-3.5 h-3.5" /> Property Calculators
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                        Do the math <span className="text-gold-400">before you pay.</span>
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg mt-4 max-w-2xl">
                        3 free calculators used by 12K+ buyers on Global Property Mart — loan eligibility, state-wise stamp duty, and net rental ROI.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="flex items-center gap-1 border-b border-navy-500/10 mb-8 overflow-x-auto" role="tablist">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(t.key)}
                                data-testid={`calc-tab-${t.key}`}
                                className={`relative shrink-0 inline-flex items-center gap-2 px-5 py-3 text-sm font-bold whitespace-nowrap transition-colors ${
                                    active ? "text-gold-700" : "text-navy-500 hover:text-gold-700"
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {t.label}
                                {active && <span className="absolute inset-x-2 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-gold-500 to-amber-400" />}
                            </button>
                        );
                    })}
                </div>

                <Active />

                <div className="mt-10 rounded-2xl bg-[#FAFAF7] border border-navy-500/10 p-5 sm:p-6 flex items-start gap-3">
                    <IndianRupee className="w-5 h-5 text-gold-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-navy-500/80 leading-relaxed">
                        <strong className="text-navy-500">Need help interpreting the numbers?</strong> Talk to a verified Home Loan Agent — <a className="text-gold-700 font-bold hover:underline" href="/loan-services">compare rates &amp; get pre-approved</a> from 20+ RBI-approved lenders on Global Property Mart.
                    </div>
                </div>
            </section>
        </div>
    );
}
